/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/itp-export/participantRows.ts
 * FILE_ID: JOBHUB-QA-ITP-PARTICIPANT-ROWS-001
 * PURPOSE: Insert and remove ITP cover participant rows in the edited document.
 */
export type ParticipantTarget =
  | { kind: 'row'; row: HTMLTableRowElement }
  | { kind: 'header'; table: HTMLTableElement }
  | { kind: 'empty'; para: HTMLElement }

function asElement(node: EventTarget | null): Element | null {
  if (node instanceof Element) return node
  if (node instanceof Node) return node.parentElement
  return null
}

export function participantTargetAt(node: EventTarget | null): ParticipantTarget | null {
  const el = asElement(node)
  if (!el) return null
  const row = el.closest('table.itp-cover-people tbody tr')
  if (row instanceof HTMLTableRowElement) return { kind: 'row', row }
  const head = el.closest('table.itp-cover-people thead')
  const table = head?.closest('table')
  if (table instanceof HTMLTableElement) return { kind: 'header', table }
  const empty = el.closest('p.itp-cover-empty')
  if (empty instanceof HTMLElement) return { kind: 'empty', para: empty }
  return null
}

function blankRow(doc: Document): HTMLTableRowElement {
  const row = doc.createElement('tr')
  for (let index = 0; index < 4; index += 1) {
    const cell = doc.createElement('td')
    cell.appendChild(doc.createElement('br'))
    row.appendChild(cell)
  }
  return row
}

export function insertParticipantRow(
  target: ParticipantTarget,
  where: 'above' | 'below',
): HTMLTableCellElement {
  if (target.kind === 'row') {
    const row = blankRow(target.row.ownerDocument)
    const parent = target.row.parentElement
    if (parent) {
      parent.insertBefore(row, where === 'above' ? target.row : target.row.nextSibling)
    }
    return row.cells[0]
  }
  if (target.kind === 'header') {
    const doc = target.table.ownerDocument
    const body = target.table.tBodies[0] ?? doc.createElement('tbody')
    if (!body.parentElement) target.table.appendChild(body)
    const row = blankRow(doc)
    body.appendChild(row)
    return row.cells[0]
  }
  const doc = target.para.ownerDocument
  const table = doc.createElement('table')
  table.className = 'itp-cover-people'
  const thead = doc.createElement('thead')
  const headRow = doc.createElement('tr')
  for (const label of ['Name', 'Role', 'Email', 'Phone']) {
    const th = doc.createElement('th')
    th.textContent = label
    headRow.appendChild(th)
  }
  thead.appendChild(headRow)
  const tbody = doc.createElement('tbody')
  const row = blankRow(doc)
  tbody.appendChild(row)
  table.append(thead, tbody)
  target.para.replaceWith(table)
  return row.cells[0]
}

export function removeParticipantRow(row: HTMLTableRowElement): void {
  const body = row.parentElement
  const table = row.closest('table')
  const doc = row.ownerDocument
  row.remove()
  if (body?.tagName === 'TBODY' && body.children.length === 0 && table) {
    const para = doc.createElement('p')
    para.className = 'itp-cover-empty'
    para.textContent = 'No participants on this job.'
    table.replaceWith(para)
  }
}

export function placeCaret(cell: HTMLTableCellElement): void {
  const doc = cell.ownerDocument
  const selection = doc.getSelection?.() ?? doc.defaultView?.getSelection() ?? null
  if (!selection) return
  const range = doc.createRange()
  range.setStart(cell, 0)
  range.collapse(true)
  selection.removeAllRanges()
  selection.addRange(range)
}
